import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Microphone, CheckCircle, Warning, ArrowsClockwise, FolderSimple,
  CaretLeft, CaretRight, X,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import RecordingModal from "../components/recordings/RecordingModal";
import MoveRecordingModal from "../components/recordings/MoveRecordingModal";
import RecordingActionsMenu from "../components/recordings/RecordingActionsMenu";
import { listRecordings, uploadRecording } from "../api/recordings";
import { getProjects } from "../api/projects";
import { getMsStatus } from "../api/microsoft";

const ONEDRIVE_DISABLED_TOOLTIP = "Connect OneDrive to enable this action";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function StatusBadge({ status }) {
  if (status === "completed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="status-badge-completed">
        <CheckCircle size={10} weight="fill" /> Completed
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="status-badge-failed">
        <Warning size={10} weight="fill" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="status-badge-processing">
      <ArrowsClockwise size={10} className="animate-spin" /> Processing
    </span>
  );
}

const EMPTY_FILTERS = { projectId: "", startDate: "", endDate: "" };

export default function RecordingsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ totalCount: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [onedriveConnected, setOnedriveConnected] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    getProjects()
      .then((data) => setProjects((data || [])))
      .catch(() => {});
    getMsStatus()
      .then(({ connected }) => setOnedriveConnected(!!connected))
      .catch(() => setOnedriveConnected(false));
  }, []);

  const fetchRecordings = useCallback(async () => {
    try {
      const res = await listRecordings({
        page,
        limit,
        projectId: filters.projectId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setRows(res.data || []);
      setPagination(res.pagination || { totalCount: 0, totalPages: 1, currentPage: 1 });
    } catch (e) {
      toast.error("Could not load recordings.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    setLoading(true);
    fetchRecordings();
  }, [fetchRecordings]);

  // Poll every 5s while any row on the current page is still processing.
  useEffect(() => {
    const hasProcessing = rows.some((r) => r.status === "processing");
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(fetchRecordings, 5000);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [rows, fetchRecordings]);

  const updateFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const filtersActive = !!(filters.projectId || filters.startDate || filters.endDate);

  const handleRecordingStopped = ({ projectId, projectName, blob, durationSeconds }) => {
    setModalOpen(false);

    uploadRecording(projectId, blob, durationSeconds)
      .then((saved) => {
        toast.success("Recording saved — AI is generating the transcript & summary.");
        // Only splice an optimistic row in when we're already looking at page 1
        // with a matching filter — otherwise just let the next fetch reconcile.
        const matchesView = page === 1 && (!filters.projectId || filters.projectId === projectId);
        if (matchesView) {
          setRows((prev) => [saved, ...prev].slice(0, limit));
          setPagination((prev) => ({ ...prev, totalCount: prev.totalCount + 1, totalPages: Math.max(prev.totalPages, 1) }));
        }
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not save the recording.");
      });
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-slate-50" data-testid="recordings-page">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">Record</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Capture in-person meetings — Gemini transcribes and summarises automatically.
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => setModalOpen(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="start-recording-btn"
          >
            <Microphone size={16} className="mr-2" weight="fill" />
            Start Recording
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Filter Bar */}
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="recordings-filter-bar">
          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Project</label>
            <Select
              value={filters.projectId || "all"}
              onValueChange={(v) => updateFilter({ projectId: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-52 h-9 bg-white" data-testid="filter-project-select">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                <SelectItem value="all" data-testid="filter-project-option-all">All Projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`filter-project-option-${p.name}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Start Date</label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => updateFilter({ startDate: e.target.value })}
              className="w-40 h-9 bg-white"
              data-testid="filter-start-date"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">End Date</label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => updateFilter({ endDate: e.target.value })}
              className="w-40 h-9 bg-white"
              data-testid="filter-end-date"
            />
          </div>

          {filtersActive && (
            <Button variant="outline" size="sm" onClick={resetFilters} className="h-9" data-testid="reset-filters-btn">
              <X size={13} className="mr-1" /> Reset Filters
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200" data-testid="recordings-table">
          <div className="flex items-center px-4 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <span className="flex-1">Project</span>
            <span className="w-40">Date</span>
            <span className="w-20 text-right">Duration</span>
            <span className="w-32 text-right">Status</span>
            <span className="w-10" />
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16" data-testid="recordings-loading">
              <ArrowsClockwise size={24} className="text-violet-500 animate-spin" />
              <p className="text-sm text-slate-500">Loading recordings...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center" data-testid="recordings-empty-state">
              <FolderSimple size={32} className="text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No recordings found.</p>
              <p className="text-xs text-slate-400 mt-1">
                {filtersActive ? "Try adjusting or resetting your filters." : "Click “Start Recording” to capture your first meeting."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/recordings/${r.id}`)}
                  data-testid={`recording-row-${r.id}`}
                >
                  <span className="flex-1 text-sm font-medium text-slate-800 truncate">
                    {r.project_name || "Untitled Project"}
                  </span>
                  <span className="w-40 text-xs text-slate-400">{formatDate(r.created_at)}</span>
                  <span className="w-20 text-right text-xs text-slate-400 tabular-nums">{formatDuration(r.duration)}</span>
                  <span className="w-32 flex justify-end">
                    <StatusBadge status={r.status} />
                  </span>
                  <span className="w-10 flex justify-end">
                    <RecordingActionsMenu
                      onMoveToFolder={() => setMoveTarget(r)}
                      moveDisabled={!onedriveConnected}
                      moveDisabledReason={ONEDRIVE_DISABLED_TOOLTIP}
                    />
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="pagination-footer">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Items per page</span>
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-16 h-7 text-xs bg-white" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500" data-testid="pagination-label">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="prev-page-btn"
              >
                <CaretLeft size={12} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                data-testid="next-page-btn"
              >
                <CaretRight size={12} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <RecordingModal
          onClose={() => setModalOpen(false)}
          onRecordingStopped={handleRecordingStopped}
        />
      )}

      {moveTarget && (
        <MoveRecordingModal
          recording={moveTarget}
          onClose={() => setMoveTarget(null)}
          onMoved={(updated) => {
            setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, folder_id: updated.folder_id } : r)));
          }}
        />
      )}
    </div>
  );
}
